import React, { useEffect, useRef } from "react";
import { useAnimation, motion } from "framer-motion";
import Button from "../Button/Button";

export interface iTabItem {
  name: string;
  value: string;
}

const transition = {
  type: "spring",
  duration: 0.5,
};

export default function Tab({
  size,
  items,
  onChange,
  defaultValue,
}: {
  size?: string;
  items: iTabItem[];
  onChange: (index: string) => void;
  defaultValue?: string;
}) {
  const tabContainerRef = useRef<HTMLDivElement>(null);
  const tabSelectorRef = useRef<HTMLDivElement>(null);
  const animation = useAnimation();

  const moveSelector = async (element: HTMLDivElement) => {
    const parentElement = element.parentElement;
    if (parentElement && tabSelectorRef.current) {
      const buttonRect = element.getBoundingClientRect();
      const parentRect = parentElement.getBoundingClientRect();
      const left = buttonRect.left - parentRect.left + parentElement.scrollLeft;
      const width = buttonRect.width;
      const height = buttonRect.height;
      await animation.start({
        left,
        width,
        height,
        transition,
      });
    }
  };

  const handleClick = async (value: string, element: HTMLDivElement) => {
    onChange(value);
    await moveSelector(element);
  };

  useEffect(() => {
    if (tabContainerRef.current) {
      // Se è stato fornito un valore predefinito, trova il pulsante corrispondente
      if (defaultValue) {
        const buttons = tabContainerRef.current.querySelectorAll(".tab-item");
        let foundButton: HTMLDivElement | null = null;
        
        buttons.forEach((button, index) => {
          if (items[index].value === defaultValue) {
            foundButton = button as HTMLDivElement;
          }
        });
        
        if (foundButton) {
          moveSelector(foundButton);
          return;
        }
      }
      
      // Se non è stato fornito un valore predefinito o non è stato trovato il pulsante corrispondente,
      // usa il primo pulsante come predefinito
      const firstButton = tabContainerRef.current.querySelector(".tab-item");
      if (firstButton) {
        moveSelector(firstButton as HTMLDivElement);
      }
    }
  }, [defaultValue, items]);

  return (
    <div className="tab-container" ref={tabContainerRef}>
      <motion.div
        ref={tabSelectorRef}
        animate={animation}
        transition={transition}
        className="tab-selector"
        style={{
          width: size ? size : "initial",
        }}
      ></motion.div>
      {items.map((item, index) => (
        <Button
          className="tab-item"
          key={index}
          text={item.name}
          variant="blank"
          onClick={(e) => handleClick(item.value, e.currentTarget as any)}
          style={{
            width: size ? size : "initial",
          }}
        />
      ))}
    </div>
  );
}
